import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Brain, Database, Layers, RefreshCw } from 'lucide-react';
interface SlidePlatformArchitectureProps {
  onContinue: () => void;
}
export function SlidePlatformArchitecture({
  onContinue
}: SlidePlatformArchitectureProps) {
  const [stage, setStage] = useState(0);
  const layers = [{
    icon: <Brain className="w-5 h-5" data-id="element-498" />,
    title: 'AI Intelligence Engine',
    desc: 'Proprietary AI models that analyze data and generate personalized content',
    chips: ['Blueprint Analysis', 'Story Generation', 'Content Creation', 'Content and Channel Optimization']
  }, {
    icon: <Database className="w-5 h-5" data-id="element-499" />,
    title: 'Data Supply Chain',
    desc: 'Secure data pipeline for regulatory-compliant content and insights',
    chips: ['Therapeutic Area Themes', 'Product and Brand Data', 'HCP & Patient Insights', 'Market Intelligence']
  }, {
    icon: <Layers className="w-5 h-5" data-id="element-500" />,
    title: 'Application Suite',
    titleSuffix: 'Foundation and Moat',
    desc: 'Core content creation and distribution platform for creating, managing, and optimizing marketing campaigns',
    chips: ['Snak Composer', 'Snakker Player — Choice Driven', 'Distribution', 'Analytics']
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 800);
    const timers = layers.map((_, idx) => setTimeout(() => setStage(3 + idx), 1300 + idx * 500));
    const final = setTimeout(() => setStage(3 + layers.length), 1300 + layers.length * 500 + 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      timers.forEach(clearTimeout);
      clearTimeout(final);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-8 md:px-20 md:py-10 max-w-7xl mx-auto" data-id="element-501">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-502">
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-503">
          Platform Architecture
        </h2>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-504">
          AI driven Closed-Loop-Marketing
        </p>
      </motion.div>

      {/* Subheader chip */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} className="flex items-center gap-4 mt-5" data-id="element-505">
        <span className="px-3 py-1 text-xs uppercase tracking-widest text-teal-300 bg-teal-400/10 border border-teal-400/30 rounded-full font-semibold" data-id="element-506">
          Platform Architecture
        </span>
        <div className="flex items-center gap-2 text-white/40 text-sm italic" data-id="element-507">
          <RefreshCw className="w-3.5 h-3.5" data-id="element-508" />
          Think · Create · Learn · Improve
        </div>
      </motion.div>

      {/* Layered stack */}
      <div className="flex-1 flex flex-col gap-3 mt-5" data-id="element-509">
        {layers.map((layer, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 3 + idx ? 1 : 0,
        x: stage >= 3 + idx ? 0 : -20
      }} transition={{
        duration: 0.5
      }} className="bg-navy-900/60 border border-teal-400/20 rounded-2xl p-5 md:p-6" data-id="element-510">
            <div className="flex items-start gap-4 mb-3" data-id="element-511">
              <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/30 flex items-center justify-center text-teal-400 flex-shrink-0" data-id="element-512">
                {layer.icon}
              </div>
              <div className="flex-1 min-w-0" data-id="element-513">
                <h3 className="text-lg md:text-xl font-bold text-teal-300" data-id="element-514">
                  {layer.title}
                  {layer.titleSuffix && <span className="text-white/50 font-medium text-base ml-2" data-id="element-515">
                      {layer.titleSuffix}
                    </span>}
                </h3>
                <p className="text-white/60 text-sm mt-0.5" data-id="element-516">{layer.desc}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-id="element-517">
              {layer.chips.map(chip => <div key={chip} className="bg-navy-800/80 border border-navy-600 rounded-lg px-3 py-2.5 text-center" data-id="element-518">
                  <span className="text-white/85 text-xs md:text-sm font-semibold" data-id="element-519">
                    {chip}
                  </span>
                </div>)}
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 3 + layers.length ? 1 : 0
    }} className="mt-5 flex justify-end" data-id="element-520">
        <Button onClick={onContinue} data-id="element-521">Traction</Button>
      </motion.div>
    </div>;
}